<template>
  <v-container class="fill-height container" fluid>
    <v-sheet elevation="1" rounded width="100%" class="mt-16 mb-8">
      <deprecation-banner v-if="productType === 'poster'" />
      <v-toolbar rounded class="mb-4">
        <h2>{{ title }}</h2>
      </v-toolbar>
      <v-row class="pa-2 mx-2 mb-5">
        <v-col cols="12" md="6" class="text-start">
          <v-form ref="certForm">
            <section>
              <v-select
                v-if="productType === 'poster'"
                :items="[
                  { label: 'Portrait', value: 'posters/portrait' },
                  { label: 'Landscape', value: 'posters/landscape' },
                ]"
                item-text="label"
                item-value="value"
                v-model="template"
                outlined
                clearable
              >
                <template #label>
                  {{ $t('Layout') }}
                  <span class="red--text"><strong>*</strong></span>
                </template>
              </v-select>
              <text-field v-model="data.mainText" clearable :label="$t('Main text')" />
              <text-field
                v-if="productType === 'poster'"
                v-model="data.subtitle"
                clearable
                :label="$t('Subtitle')"
              />
              <text-field
                v-if="productType === 'poster'"
                v-model="data.fullAddress"
                clearable
                :label="$t('Full Address')"
              />
              <section key="date-picker-section">
                <v-row>
                  <v-col cols="12" md="8">
                    <date-picker
                      v-model="data.date"
                      :label="$t('Date')"
                      format="DD.MM.YYYY"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex align-center">
                    <v-checkbox
                      v-model="showTime"
                      :label="$t('Show Time')"
                      class="mt-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row v-if="showTime">
                  <v-col cols="12" md="6">
                    <v-menu
                      ref="timeMenu"
                      v-model="timeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="selectedTime"
                          :label="$t('Time')"
                          prepend-icon="mdi-clock-time-four-outline"
                          readonly
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        v-if="timeMenu"
                        v-model="selectedTime"
                        full-width
                        format="24hr"
                        @click:minute="timeMenu = false"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </section>
              <v-select
                v-if="productType === 'poster'"
                :items="sizes"
                item-text="label"
                item-value="value"
                v-model="data.size"
                outlined
                clearable
              >
                <template #label>
                  {{ $t('Size') }}
                </template>
              </v-select>
              <v-select
                :items="langs"
                item-text="label"
                item-value="value"
                v-model="data.lang"
                outlined
                clearable
              >
                <template #label>
                  {{ $t('Language') }}
                </template>
              </v-select>
              <v-row justify="space-between" class="px-3">
                <v-checkbox
                  v-model="data.elements.constellations"
                  :label="$t('Constellations')"
                  class="mb-2"
                ></v-checkbox>
                <v-checkbox
                  v-model="data.elements.graticule"
                  :label="$t('Graticule')"
                  class="mb-2"
                ></v-checkbox>
                <v-checkbox
                  v-if="productType === 'poster'"
                  v-model="data.elements.milkyWay"
                  :label="$t('MilkyWay')"
                  class="mb-2"
                ></v-checkbox>
                <v-checkbox
                  v-else
                  v-model="data.elements.stars"
                  :label="$t('Stars')"
                  class="mb-2"
                ></v-checkbox>
              </v-row>
              <v-checkbox
                v-model="customCoordinates"
                :label="$t('Custom Coordinates')"
              ></v-checkbox>
              <section v-if="customCoordinates">
                <v-row>
                  <v-col cols="12" md="6" class="text-end">
                    <text-field
                      v-model="data.coords.lat"
                      clearable
                      :label="$t('Lattitude')"
                      required
                      type="number"
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="text-end">
                    <text-field
                      v-model="data.coords.lng"
                      clearable
                      :label="$t('Longitude')"
                      required
                      type="number"
                    />
                  </v-col>
                </v-row>
              </section>
              <section v-else>
                <select-location
                  v-model="data.coords"
                  placeholder="Location"
                  @update:value="
                    (location) => {
                      data.coords.lat = location?.lat || null
                      data.coords.lng = location?.lng || null
                    }
                  "
                />
              </section>

              <v-list v-if="productType === 'poster'" class="mb-5" outlined nav>
                <v-subheader>
                  {{ $t('Color') }}
                  <span class="red--text ml-1"><strong>*</strong></span>
                </v-subheader>
                <v-list-item-group v-model="selectedColor" color="primary">
                  <v-row justify="space-between">
                    <v-col cols="3" v-for="color in colors" :key="color.id">
                      <v-list-item :value="color.id" class="p-16-0">
                        <div class="color-container">
                          <div class="color" :style="{ backgroundColor: color.color }"></div>
                          <span class="text-center mt-2">
                            {{ color.title }}
                          </span>
                        </div>
                      </v-list-item>
                    </v-col>
                  </v-row>
                </v-list-item-group>
              </v-list>
              <v-list v-if="productType === 'poster'" class="mb-5" outlined nav>
                <v-subheader>
                  {{ $t('Poster Design') }}
                  <span class="red--text ml-1"><strong>*</strong></span>
                </v-subheader>
                <v-list-item-group v-model="selectedDesign" color="primary">
                  <v-row justify="space-between">
                    <v-col cols="3" v-for="design in designs" :key="design.id">
                      <v-list-item :value="design.id" class="p-16-0">
                        <div class="color-box" :style="{ backgroundColor: design.backgroundColor }">
                          <div class="color-inner" :style="{ borderColor: design.textColor }">
                            <div
                              class="color-circle"
                              :style="{
                                backgroundColor: design.mapBackgroundColor,
                                borderColor: design.textColor,
                              }"
                            ></div>
                            <div
                              class="color-line"
                              :style="{ backgroundColor: design.textColor }"
                            ></div>
                          </div>
                        </div>
                      </v-list-item>
                    </v-col>
                  </v-row>
                </v-list-item-group>
              </v-list>
            </section>
            <v-btn
              :loading="loading"
              :disabled="!isValid"
              @click="generateProduct"
              color="primary"
              block
            >
              {{ $t('Generate') }}
            </v-btn>
          </v-form>
        </v-col>
      </v-row>
    </v-sheet>
  </v-container>
</template>

<script>
  import swh from '@/assets/js/sw_helper'
  import datePicker from '../components/DatePicker'
  import TextField from '../components/TextField'
  import SelectLocation from '../components/SelectLocation'
  import DeprecationBanner from '../components/DeprecationBanner.vue'
  import { createSkymapProduct } from '../services/pdfGenerationService'

  export default {
    props: {
      source: String,
    },
    data() {
      return {
        loading: false,
        productType: 'poster',
        title: '',
        sizes: [
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
        ],
        langs: [
          { label: 'English', value: 'en' },
          { label: 'German', value: 'de' },
          { label: 'Spanish', value: 'es' },
          { label: 'French', value: 'fr' },
          { label: 'Italian', value: 'it' },
          { label: 'Dutch', value: 'nl' },
        ],
        colors: [
          {
            id: 'color-1',
            title: 'Midnight Blue',
            color: '#1b1d28',
          },
          {
            id: 'color-2',
            title: 'Velvet Black',
            color: '#000000',
          },
          {
            id: 'color-3',
            title: 'Gray Blue',
            color: '#3e4655',
          },
          {
            id: 'color-4',
            title: 'Deep Sea Green',
            color: '#2d4855',
          },
        ],
        selectedColor: null,
        selectedDesign: null,
        template: '',
        customCoordinates: false,
        showTime: false,
        selectedTime: '12:00',
        timeMenu: false,
        data: {
          mainText: '',
          subtitle: '',
          fullAddress: '',
          date: new Date().toISOString().substring(0, 10),
          size: 'small',
          lang: 'en',
          colors: {
            backgroundMain: '',
            foregroundMain: '',
            borderMain: '',
            backgroundCelestial: '',
            foregroundCelestial: '',
            borderCelestial: '',
          },
          elements: {
            constellations: false,
            graticule: false,
            milkyWay: false,
            stars: false,
          },
          coords: {
            lat: null,
            lng: null,
          },
        },
        apiUrl: `${process.env.VUE_APP_ISR_DOWNLOAD_SERVICE}/`,
      }
    },
    methods: {
      parseDate(dateString) {
        return swh.parseDate(dateString)
      },
      async generateProduct() {
        this.loading = true

        const date = this.data.date.includes('.') ? this.parseDate(this.data.date) : this.data.date

        // Combine date and time if showTime is true
        let finalDate = new Date(date)
        if (this.showTime && this.selectedTime) {
          const [hours, minutes] = this.selectedTime.split(':')
          finalDate.setUTCHours(parseInt(hours), parseInt(minutes), 0, 0)
        }

        let requestData = {
          mainText: this.data.mainText || '',
          date: Math.floor(finalDate.getTime() / 1000), // Unix timestamp
          dateFormat: this.showTime ? 'DD. MMMM YYYY, HH:mm' : 'DD. MMMM YYYY',
          lang: this.data.lang || 'en',
          elements: this.data.elements,
          coords: {
            lat: parseFloat(this.data.coords.lat),
            lng: parseFloat(this.data.coords.lng),
          },
        }

        console.log(requestData)

        if (this.productType === 'poster') {
          const design = this.designs.find((c) => c.id === this.selectedDesign)
          requestData = {
            ...requestData,
            subtitle: this.data.subtitle || '',
            fullAddress: this.data.fullAddress || '',
            size: this.data.size || 'small',
            colors: {
              backgroundMain: design.backgroundColor,
              foregroundMain: design.textColor,
              borderMain: design.borderColor,
              backgroundCelestial: design.mapBackgroundColor,
              foregroundCelestial: design.mapBorderColor,
              borderCelestial: design.mapBorderColor,
            },
          }
        }

        const template = this.productType === 'poster' ? this.template : 'jewelry/skymap'
        const filename =
          this.productType === 'poster'
            ? `skymap_poster_${this.template.split('/')[1]}_${this.data.size}_${new Date()
                .toISOString()
                .replace(/[-:/]/g, '')
                .slice(0, 15)}.pdf`
            : `skymap_jewelry_${new Date().toISOString().replace(/[-:/]/g, '').slice(0, 15)}.pdf`
        const apiPath = this.productType === 'poster' ? `/posters/generate` : `/jewelry/generate`

        try {
          const { data } = await createSkymapProduct(apiPath, template, requestData, filename)

          if (data?.data?.length) {
            data.data.forEach((file) => {
              window.open(file.url, '_blank')
            })
          } else {
            window.open(data.data.url, '_blank')
          }
        } catch (err) {
          console.log(err)
        } finally {
          this.loading = false
        }
      },
      updateProductType() {
        const pathSegments = this.$route.path.split('/')
        const productType = pathSegments[pathSegments.length - 1]

        this.productType = productType === 'jewelry' ? 'jewelry' : 'poster'
        this.title = productType === 'poster' ? this.$t('Custom Poster') : this.$t('Custom Jewelry')

        // Reset fields when product type changes
        this.template = ''
        this.selectedColor = null
        this.selectedDesign = null
        this.customCoordinates = false
        this.showTime = false
        this.selectedTime = '12:00'
        this.timeMenu = false
        Object.assign(this.data, {
          mainText: '',
          subtitle: '',
          fullAddress: '',
          date: new Date().toISOString().substring(0, 10),
          size: 'small',
          lang: 'en',
          colors: {
            backgroundMain: '',
            foregroundMain: '',
            borderMain: '',
            backgroundCelestial: '',
            foregroundCelestial: '',
            borderCelestial: '',
          },
          elements: {
            constellations: false,
            graticule: false,
            milkyWay: false,
            stars: false,
          },
          coords: {
            lat: null,
            lng: null,
          },
        })
      },
    },
    computed: {
      designs: function () {
        const primaryColor =
          this.colors.find((color) => color.id === this.selectedColor)?.color || '#000000'
        const secondaryColor = '#ffffff'

        return [
          {
            id: 'design-1',
            backgroundColor: primaryColor,
            borderColor: secondaryColor,
            textColor: secondaryColor,
            mapBackgroundColor: primaryColor,
            mapBorderColor: secondaryColor,
          },
          {
            id: 'design-2',
            backgroundColor: primaryColor,
            borderColor: secondaryColor,
            textColor: secondaryColor,
            mapBackgroundColor: secondaryColor,
            mapBorderColor: primaryColor,
          },
          {
            id: 'design-3',
            backgroundColor: secondaryColor,
            borderColor: primaryColor,
            textColor: primaryColor,
            mapBackgroundColor: primaryColor,
            mapBorderColor: secondaryColor,
          },
          {
            id: 'design-4',
            backgroundColor: secondaryColor,
            borderColor: primaryColor,
            textColor: primaryColor,
            mapBackgroundColor: secondaryColor,
            mapBorderColor: primaryColor,
          },
        ]
      },
      isValid: function () {
        let valid = true
        if (!this.data.date) valid = false
        if (!this.data.coords.lat) valid = false
        if (!this.data.coords.lng) valid = false

        if (this.productType === 'poster') {
          if (!this.template) valid = false
          if (!this.selectedColor) valid = false
          if (!this.selectedDesign) valid = false
        }

        return valid
      },
    },
    watch: {
      $route: {
        immediate: true,
        handler() {
          this.updateProductType()
        },
      },
      customCoordinates() {
        this.data.coords.lat = null
        this.data.coords.lng = null
      },
    },

    components: { datePicker, TextField, SelectLocation, DeprecationBanner },
  }
</script>

<style scoped>
  .container {
    opacity: 0.94 !important;
  }

  .p-16-0 {
    padding: 16px 0px;
  }

  .color-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 0.875rem;
    width: 100%;
  }

  .color-container .color {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid #fff;
  }

  .color-box {
    width: 48px;
    height: 60px;
    border: 1px solid #000;
    margin: 0 auto;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  }

  .color-inner {
    width: 42px;
    height: 54px;
    margin: 0.125rem auto 0 auto;
    border: 1px solid;
  }

  .color-circle {
    width: 28px;
    height: 28px;
    margin: 0.375rem auto 0 auto;
    border: 1px solid;
    border-radius: 50%;
  }

  .color-line {
    width: 6px;
    height: 1.5px;
    margin: 0.5rem auto 0 auto;
  }
</style>
